import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X, ChevronRight, ChevronLeft, CheckCircle2, Circle,
  Rocket, Settings, BookOpen, ImageIcon, Globe, Users, PartyPopper,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

// ─── Tour steps (highlights sidebar items) ────────────────────────────────────
const TOUR_STEPS = [
  {
    title: "Bienvenido a MenuGenius!",
    description: "Te vamos a ensenar las secciones principales de la plataforma en 30 segundos.",
    icon: Rocket,
    position: "center",
  },
  {
    title: "Panel de Control",
    description: "Tu panel principal con metricas del restaurante, alertas y actividad reciente.",
    icon: Rocket,
    target: '[data-testid="nav-link-dashboard"]',
    position: "right",
  },
  {
    title: "Menus y Carta",
    description: "Crea tu carta con categorias y platos. Genera imagenes con IA automaticamente.",
    icon: BookOpen,
    target: '[data-testid="nav-link-menus"]',
    position: "right",
  },
  {
    title: "Tu pagina web",
    description: "Publica una web profesional para tu restaurante con un solo clic. Elige entre 5 temas.",
    icon: Globe,
    target: '[data-testid="nav-link-web"]',
    position: "right",
  },
  {
    title: "Equipo",
    description: "Invita a tu equipo y asigna roles con permisos especificos para cada modulo.",
    icon: Users,
    target: '[data-testid="nav-link-equipo"]',
    position: "right",
  },
  {
    title: "Listo para empezar!",
    description: "Completa el checklist de configuracion para sacar el maximo partido. Lo encontraras en tu dashboard.",
    icon: PartyPopper,
    position: "center",
  },
];

// ─── Onboarding checklist items ───────────────────────────────────────────────
const CHECKLIST_ITEMS = [
  { key: "restaurant_info", label: "Completa los datos del restaurante", path: "/app/settings", icon: Settings },
  { key: "first_menu", label: "Crea tu primer menu", path: "/app/menus", icon: BookOpen },
  { key: "generate_images", label: "Genera imagenes de platos con IA", path: "/app/menus", icon: ImageIcon },
  { key: "activate_website", label: "Activa tu pagina web", path: "/app/website", icon: Globe },
  { key: "invite_team", label: "Invita a un miembro del equipo", path: "/app/team", icon: Users },
];

// ─── Tour overlay ─────────────────────────────────────────────────────────────
function TourOverlay({ onComplete }) {
  const [step, setStep] = useState(0);
  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;

  // Highlight target element
  useEffect(() => {
    if (!current.target) return;
    const el = document.querySelector(current.target);
    if (el) {
      el.style.position = "relative";
      el.style.zIndex = "1001";
      el.style.boxShadow = "0 0 0 4px hsl(var(--primary) / 0.4), 0 0 24px hsl(var(--primary) / 0.2)";
      el.style.borderRadius = "12px";
      el.style.transition = "box-shadow 0.3s";
    }
    return () => {
      if (el) {
        el.style.zIndex = "";
        el.style.boxShadow = "";
      }
    };
  }, [step, current.target]);

  const next = () => {
    if (isLast) { onComplete(); return; }
    setStep(s => s + 1);
  };
  const prev = () => setStep(s => Math.max(0, s - 1));

  return (
    <div className="fixed inset-0 z-[1000]" data-testid="tour-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onComplete} />

      {/* Tooltip card */}
      <div
        className={`absolute z-[1002] bg-card border border-border rounded-2xl shadow-2xl p-5 w-[340px]
          ${current.position === "center"
            ? "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            : "top-1/2 left-[280px] -translate-y-1/2"
          }`}
        style={{ animation: "fadeSlideIn 0.25s ease-out" }}
        data-testid={`tour-step-${step}`}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm">{current.title}</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{current.description}</p>
          </div>
          <button onClick={onComplete} className="text-muted-foreground hover:text-foreground -mt-1" data-testid="tour-skip">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? "bg-primary" : i < step ? "bg-primary/40" : "bg-muted"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {!isFirst && (
              <Button size="sm" variant="ghost" onClick={prev} className="h-8 text-xs gap-1">
                <ChevronLeft className="w-3 h-3" /> Anterior
              </Button>
            )}
            <Button size="sm" onClick={next} className="h-8 text-xs gap-1" data-testid="tour-next">
              {isLast ? "Empezar" : "Siguiente"} {!isLast && <ChevronRight className="w-3 h-3" />}
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px) translateX(-50%); }
          to { opacity: 1; transform: translateY(-50%) translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ─── Onboarding Checklist (shown on dashboard) ───────────────────────────────
export function OnboardingChecklist() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/help/onboarding")
      .then(d => setProgress(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleStep = async (key) => {
    const current = progress?.steps?.[key] || false;
    try {
      const updated = await api.post("/help/onboarding", { step: key, completed: !current });
      setProgress(updated);
    } catch { /* ignore */ }
  };

  if (loading || !progress || dismissed) return null;

  const completedCount = CHECKLIST_ITEMS.filter(i => progress.steps?.[i.key]).length;
  const allDone = completedCount === CHECKLIST_ITEMS.length;
  const pct = Math.round((completedCount / CHECKLIST_ITEMS.length) * 100);

  if (allDone) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-5 relative" data-testid="onboarding-checklist">
      <button onClick={() => setDismissed(true)} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <Rocket className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-sm">Configura tu restaurante</h3>
          <p className="text-[10px] text-muted-foreground">{completedCount} de {CHECKLIST_ITEMS.length} completados</p>
        </div>
        <span className="text-xs font-bold text-primary">{pct}%</span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-muted rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Steps */}
      <div className="space-y-1">
        {CHECKLIST_ITEMS.map(item => {
          const done = progress.steps?.[item.key] || false;
          return (
            <div key={item.key} className="flex items-center gap-3 group" data-testid={`checklist-${item.key}`}>
              <button
                onClick={() => toggleStep(item.key)}
                className="flex-shrink-0 transition-transform hover:scale-110"
                data-testid={`check-${item.key}`}
              >
                {done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  : <Circle className="w-5 h-5 text-muted-foreground/40" />
                }
              </button>
              <button
                onClick={() => navigate(item.path)}
                className={`flex-1 text-left text-sm transition-colors
                  ${done ? "text-muted-foreground line-through" : "text-foreground group-hover:text-primary"}`}
              >
                {item.label}
              </button>
              {!done && (
                <button onClick={() => navigate(item.path)} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Onboarding Manager ──────────────────────────────────────────────────
export default function OnboardingManager() {
  const [showTour, setShowTour] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Only check on dashboard route
    if (location.pathname !== "/app" && location.pathname !== "/app/") return;

    api.get("/help/onboarding")
      .then(d => {
        if (!d.tour_completed) setShowTour(true);
      })
      .catch(() => {});
  }, [location.pathname]);

  const completeTour = useCallback(async () => {
    setShowTour(false);
    try { await api.post("/help/onboarding", { tour_completed: true }); } catch { /* ignore */ }
  }, []);

  if (!showTour) return null;
  return <TourOverlay onComplete={completeTour} />;
}
